import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowDown, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonStyles } from '@/lib/button-styles'
import Starburst from '@/components/generals/starburst'
import RotatingBadge from '@/components/landing/rotating-badge'
import { HERO_PROOF_IMAGES } from '@/constants/landing'

// Load-in motion for the hero. Skipped entirely when the visitor prefers reduced motion.
const ENTER = 'motion-safe:animate-in fade-in slide-in-from-bottom-10 fill-mode-both duration-700 ease-out'

const HeroSection: FC = (): ReactNode => {
    return (
        <section id='top' aria-labelledby='hero-title' className='site-container pt-6 pb-16 md:pt-10 md:pb-24'>
            <div className={cn('flex flex-wrap items-center justify-between gap-x-6 gap-y-2 font-mono text-xs font-medium tracking-[0.18em] uppercase', ENTER)}>
                <p>
                    Brand &amp; packaging studio
                </p>

                <p className='inline-flex items-center gap-2'>
                    <span className='relative flex size-2.5'>
                        <span className='absolute inline-flex size-full rounded-full bg-primary opacity-75 motion-safe:animate-ping' />
                        <span className='relative inline-flex size-2.5 rounded-full bg-primary' />
                    </span>
                    Booking projects for spring 2027
                </p>
            </div>

            <h1
                id='hero-title'
                className='mt-6 font-heading text-[clamp(3.25rem,13.5vw,13.5rem)] leading-[0.82] font-black tracking-[-0.015em] uppercase wdth-75 md:mt-10'
            >
                <span className={cn('block', ENTER)}>
                    Brands you
                </span>

                <span className={cn('block text-right', ENTER, 'delay-150')}>
                    Can’t walk
                </span>

                <span className={cn('block', ENTER, 'delay-300')}>
                    <span className='relative mr-[0.14em] inline-block h-[0.72em] w-[1.55em] overflow-hidden rounded-full bg-pop-pink align-baseline'>
                        <Image
                            src='/images/work/fizz-theory.png'
                            alt=''
                            fill
                            loading='eager'
                            sizes='(min-width: 100rem) 21rem, 21vw'
                            className='object-cover'
                        />
                    </span>
                    Past<span className='text-pop-orange'>.</span>
                    <Starburst className='ml-[0.1em] inline-block size-[0.6em] align-baseline text-primary motion-safe:animate-spin-slow' />
                </span>
            </h1>

            <div className='mt-10 grid gap-10 md:mt-14 lg:grid-cols-12 lg:items-end'>
                <div className={cn('lg:col-span-6 xl:col-span-5', ENTER, 'delay-500')}>
                    <p className='max-w-xl text-lg leading-relaxed text-pretty md:text-xl'>
                        Wallop is an independent branding and packaging studio. We build identities, packs and photoreal product renders for consumer brands that refuse to blend into the shelf.
                    </p>

                    <div className='mt-8 flex items-center gap-4'>
                        <div className='flex -space-x-3'>
                            {HERO_PROOF_IMAGES.map(image => (
                                <span
                                    key={image}
                                    className='relative size-11 overflow-hidden rounded-full border-2 border-background bg-muted'
                                >
                                    <Image src={image} alt='' fill sizes='48px' className='object-cover' />
                                </span>
                            ))}
                        </div>

                        <p className='text-sm leading-snug'>
                            <span className='block font-bold'>
                                160+ brands launched
                            </span>
                            <span className='block text-muted-foreground'>
                                From soda to skincare, since 2014
                            </span>
                        </p>
                    </div>
                </div>

                <div className={cn('flex flex-wrap gap-3 lg:col-span-6 lg:justify-end xl:col-span-7', ENTER, 'delay-700')}>
                    <a href='#contact' className={buttonStyles({ size: 'lg' })}>
                        Start a project
                        <ArrowUpRight aria-hidden='true' className='size-5 transition-transform duration-300 group-hover/button:rotate-45' />
                    </a>

                    <a href='#work' className={buttonStyles({ variant: 'outline', size: 'lg' })}>
                        See the work
                    </a>
                </div>
            </div>

            <div className={cn('relative mt-12 md:mt-16', ENTER, 'delay-700')}>
                <div className='relative aspect-[4/3] overflow-hidden rounded-[1.75rem] bg-pop-blue sm:aspect-[16/9] md:rounded-[2.75rem]'>
                    <Image
                        src='/images/hero-lineup.png'
                        alt='A floating lineup of packaging by Wallop: soda cans, a hot sauce bottle, an oat milk carton, a cereal box, a skincare tube and a coffee bag on a cobalt background'
                        fill
                        sizes='(min-width: 100rem) 94rem, 94vw'
                        className='object-cover'
                    />

                    <p className='absolute top-6 left-6 hidden -rotate-6 rounded-2xl bg-pop-yellow px-5 py-3 font-heading text-xl leading-[0.95] font-black uppercase shadow-[0_14px_30px_-14px_rgba(0,0,0,0.45)] wdth-80 md:block lg:top-10 lg:left-10 lg:text-2xl'>
                        Rendered,
                        <br />
                        not shot
                    </p>

                    <p className='absolute bottom-4 left-4 rounded-full bg-background px-4 py-2 font-mono text-[0.68rem] tracking-[0.18em] uppercase md:bottom-8 md:left-8 md:text-xs'>
                        Fig. 01 / The 2026 lineup
                    </p>

                    <a
                        href='#work'
                        aria-label='See the work'
                        className='group absolute top-4 right-4 size-24 rounded-full bg-primary text-primary-foreground transition-colors hover:bg-background focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-background md:top-8 md:right-8 md:size-36'
                    >
                        <RotatingBadge
                            id='hero-badge'
                            text='BRAND • PACKAGING • 3D RENDERS • LAUNCH • '
                            className='size-full'
                        >
                            <ArrowDown aria-hidden='true' className='size-6 transition-transform duration-300 group-hover:translate-y-1 md:size-9' />
                        </RotatingBadge>
                    </a>
                </div>
            </div>
        </section>
    )
}

export default HeroSection
